// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { RegistrationCodeValidationResultModel, RegistrationCodeValidationResults, RegistrationResultModel } from '../../../models';
import { SelfRegistrationBaseService } from './self-registration-base.service';
import { AppService } from '../app.service';
import { EnvService } from '../env.service';
import { OidcService } from '../account/oidc.service';
import { environment } from '../../../../environments/environment';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class SelfRegistrationService extends SelfRegistrationBaseService {

  private isRequesting: boolean = false;

  constructor(
    private _appService: AppService,
    private _http: HttpClient,
    _envService: EnvService,
    _oidcService: OidcService
  ) {
    super(_envService, _oidcService);
  }

  public override validateRegistrationCode(registrationCode: string): void {
    if (!registrationCode || this.isRequesting) {
      return;
    }

    this.isRequesting = true;
    this._appService.setLoading(true);
    this._registrationCode$.next(registrationCode.trim());

    const url =
    environment.apiConfig.surveyApiUrl +
    environment.apiConfig.registrationValidateRegistrationCodeApi +
    `?registrationCode=${registrationCode}`;

    this._http
      .get<RegistrationCodeValidationResultModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError((err, caught): Observable<RegistrationCodeValidationResultModel> => {
          return of({
            validationResult: RegistrationCodeValidationResults.unknownValidationFailure,
            registrationCode: registrationCode,
            participantId: null
          } as RegistrationCodeValidationResultModel);
        })
      )
      .subscribe((result) => {
        this._appService.setLoading(false);
        this.isRequesting = false;
        this._validationResult$.next(result);
      });
  }

  public override register(registrationCode: string): void {
    if (!registrationCode || this.isRequesting) {
      return;
    }

    this.isRequesting = true;
    this._appService.setLoading(true);

    const url =
    environment.apiConfig.surveyApiUrl +
    environment.apiConfig.registrationApi;

    const request = { registrationCode: registrationCode };

    this._http.post<RegistrationResultModel>(url, request)
    .pipe(
      retryBackoff(environment.retryConfig),
      catchError((err, caught): Observable<RegistrationResultModel> => {
        let validationResult = RegistrationCodeValidationResults.unknownValidationFailure;

        if ((err as HttpErrorResponse).status !== undefined) {
          if (err.status === 404) {
            validationResult = RegistrationCodeValidationResults.campaignNotFound;
          } else if (err.status === 400) {
            validationResult = RegistrationCodeValidationResults.registrationCodeExpired;
          }
        }
        return of({
          validationResult: validationResult,
          errorMessage: err.error.errorMessage,
        } as RegistrationResultModel);
      })
    ).subscribe((result) => {
      this.isRequesting = false;
      this._appService.setLoading(false);
      this._registrationResult$.next(result);
    });
  }
}
